<script setup lang="ts">
import CDashboardHeader from '@/presentation/components/dashboard/CDashboardHeader.vue'
import { useRouter } from 'vue-router'
import { onMounted, ref } from 'vue'
import { useAuthStore } from '@/presentation/stores/auth.ts'
import { storeToRefs } from 'pinia'
import { useSchoolStore } from '@/presentation/stores/school/school.ts'
import { useProfileStore } from '@/presentation/stores/profile/profile.ts'
import PProgressSpinner from 'primevue/progressspinner'

// COMPOSABLES
const router = useRouter()
const authStore = useAuthStore()
const schoolStore = useSchoolStore()
const profileStore = useProfileStore()

// STORES STATE
const { isTeacher } = storeToRefs(authStore)
const { schoolList } = storeToRefs(schoolStore)
const { me } = storeToRefs(profileStore)

// LOCAL STATE
const loading = ref(true)
const error = ref<string | null>(null)
const redirectAttempted = ref(false)

// METHODS
/**
 * Trouve la classe par défaut d'un enseignant
 * @returns La classe par défaut ou la première classe trouvée
 */
function getDefaultClassroom() {
  // Vérifier que la liste des écoles n'est pas vide
  if (!schoolList.value.length) {
    return null
  }

  const defaultClassroom = schoolList.value[0]?.classrooms.find((classroom) => {
    // Vérifier que classroom.groups existe et n'est pas vide
    if (!classroom.groups || !classroom.groups.length) {
      return false
    }

    const classroomMonitors = classroom.groups
      .filter((group) => group.teacher) // Vérifier que le groupe a un enseignant
      .map((group) => group.teacher?.userIdentifier)

    return me.value?.identifier && classroomMonitors.includes(me.value.identifier)
  })

  return defaultClassroom ?? schoolList.value[0]?.classrooms[0]
}

/**
 * Tente de rediriger l'utilisateur vers sa classe par défaut
 * seulement s'il s'agit d'un enseignant
 */
async function redirectToDefaultClassroom() {
  if (redirectAttempted.value) return
  redirectAttempted.value = true

  try {
    // Vérifier si l'utilisateur est un enseignant
    if (!isTeacher.value) {
      // Si ce n'est pas un enseignant, arrêter ici
      loading.value = false
      return
    }

    // S'assurer que les écoles sont chargées
    if (schoolList.value.length === 0) {
      // Si les écoles ne sont pas encore chargées, les charger
      await schoolStore.fetchSchoolsForMe()
    }

    // Trouver la classe par défaut pour l'enseignant
    const defaultClassroom = getDefaultClassroom()
    if (defaultClassroom && schoolList.value.length > 0) {
      await router.push({
        name: 'dashboard-group',
        params: {
          schoolId: schoolList.value[0].id,
          classroomId: defaultClassroom.schoolClassroomId
        }
      })
    }
  } catch (err) {
    console.error('Erreur lors de la redirection vers la classe par défaut:', err)
    error.value = 'Impossible de charger votre classe par défaut'
  } finally {
    loading.value = false
  }
}

// LIFECYCLE HOOKS
onMounted(async () => {
  try {
    await redirectToDefaultClassroom()
  } catch (err) {
    console.error('Erreur dans onMounted:', err)
  } finally {
    loading.value = false
  }
})
</script>

<template>
  <c-dashboard-header />

  <div class="col-span-12 flex flex-1 flex-col gap-8 overflow-y-auto p-8 pt-2">
    <!-- État de chargement -->
    <div v-if="loading" class="flex flex-col items-center justify-center p-8">
      <p-progress-spinner style="width: 50px; height: 50px" strokeWidth="5" />
      <p class="mt-4 text-lg">Chargement du tableau de bord...</p>
    </div>

    <!-- Contenu du dashboard -->
    <router-view v-else />
  </div>
</template>
